import { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { useSelector } from 'react-redux';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  selectMedicalInfoAnswers,
  updateAssessment,
} from '../../../redux/assessment';
import { selectPresentMeta } from '../../../redux/meta';
import { useAppDispatch } from '../../../pages/assessment';
import {
  DispatchUpdateAssessmentInput,
  QuestionTypes,
  Screens,
} from '../../../redux/types';
import { AssessmentButtons, AssessmentCard } from '../shared';
import { rules } from '../demographics/rules';
import { ReasonVisitAnswer } from '../../../clinical/types';
import { selectLdFlags } from '../../../redux/ld-flags';

type FormValues = {
  checkedItems: string[];
};

const OPTIONS_OVERRIDE: Partial<Record<Screens, ReasonVisitAnswer[]>> = {
  [Screens.MI_HaveAppointment]: ['Yes', 'No, not yet'],
};

const Question = () => {
  const dispatch = useAppDispatch();
  const meta = useSelector(selectPresentMeta);
  const medicalInfoAnswers = useSelector(selectMedicalInfoAnswers);
  const { shouldUseIntakeV2Point1 } = useSelector(selectLdFlags);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const { addToast } = useToasts();

  useEffect(() => {
    const result = medicalInfoAnswers[meta.screen] || [];
    // set checked items?
    setIsReadOnly(result.length > 0 ? !meta.canRewrite : false);
  }, [medicalInfoAnswers, meta.screen, meta.canRewrite]);

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
    reset,
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      checkedItems: medicalInfoAnswers[meta.screen] || [],
    },
  });

  const options =
    meta.options &&
    shouldUseIntakeV2Point1 &&
    OPTIONS_OVERRIDE[meta.screen] !== undefined
      ? OPTIONS_OVERRIDE[meta.screen]
      : meta.options;

  const onSubmit: SubmitHandler<FormValues> = async data => {
    try {
      const answer: DispatchUpdateAssessmentInput = [
        { questionCode: meta.screen, answerValue: data.checkedItems },
      ];
      await dispatch(updateAssessment(answer));
    } catch (e) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  useEffect(() => {
    reset({ checkedItems: medicalInfoAnswers[meta.screen] || [] });
  }, [meta, isSubmitSuccessful]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AssessmentCard>
        {meta.type === QuestionTypes.One && (
          <Controller
            name="checkedItems"
            control={control}
            rules={rules.requiredCheckboxOrRadio as any}
            render={({ field, fieldState }) => (
              <FormControl error={!!fieldState.error}>
                <RadioGroup {...field}>
                  {options?.map((option, index) => (
                    <FormControlLabel
                      control={<Radio />}
                      disabled={isReadOnly}
                      key={`assessment-radio-key${index}`}
                      label={option}
                      value={option}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          />
        )}
      </AssessmentCard>
      <AssessmentButtons
        primary={{
          children: 'Continue',
          type: 'submit',
          disabled: !isValid || isSubmitting,
          loading: isSubmitting,
        }}
      />
    </form>
  );
};

export default Question;
